import React from "react";
import ReactDOMServer from "react-dom/server";
import errorService from "./errorService";
import VideoDownloadPage from "../../components/common/VideoDownloadPage";
import ImageDownloadPage from "../../components/common/ImageDownloadPage";

interface DownloadMediaFileOptions {
  url: string;
  mediaType: "image" | "video";
  filename?: string;
  extension?: string;
  organization?: {
    name: string;
  };
}

const createVideoDownloadPage = (videoUrl: string): string => ReactDOMServer.renderToString(
  <VideoDownloadPage videoUrl={videoUrl} />
);

const createImageDownloadPage = (imageUrl: string): string => ReactDOMServer.renderToString(
  <ImageDownloadPage imageUrl={imageUrl} />
);

const downloadMediaFile = async ({
  url,
  mediaType,
  filename,
  extension,
  organization
}: DownloadMediaFileOptions): Promise<void> => {
  const timestamp = Date.now();
  const defaultExtension = extension || (mediaType === "image" ? "jpg" : "mp4");
  const fileName = filename || `${timestamp}_${mediaType === "image" ? "photo" : "video"}.${defaultExtension}`;

  try {
    if (organization?.name === "Lloyds Banking Group") {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // For other organizations, normal download
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();

    reader.onloadend = () => {
      const timestamp = Date.now();
      const link = document.createElement("a");

      if (typeof reader.result === "string") {
        link.href = reader.result;

        // Use custom filename or generate default one
        if (filename) {
          link.download = filename;
        } else {
          const defaultExtension = extension || (mediaType === "image" ? "jpg" : "mp4");
          link.download = `${timestamp}_${
            mediaType === "image" ? "photo" : "video"
          }.${defaultExtension}`;
        }

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
        }, 200);
      }
    };

    reader.readAsDataURL(blob);
  } catch (error) {
    // Special logic for Lloyds Banking Group - open in new tab in case of error
    if (organization?.name === "Lloyds Banking Group") {
      try {
        // Create HTML content based on media type
        const htmlContent = mediaType === "video"
          ? createVideoDownloadPage(url)
          : createImageDownloadPage(url);

        const blob = new Blob([htmlContent], { type: "text/html" });
        const htmlUrl = URL.createObjectURL(blob);

        // Use only window.open, no fallbacks to prevent double tabs
        const newWindow = window.open(htmlUrl, "_blank", "noopener,noreferrer");
        if (newWindow) {
          // Successfully opened in new window
          setTimeout(() => URL.revokeObjectURL(htmlUrl), 5000);
        } else {
          // If window.open fails, just revoke the URL and don't try alternatives
          URL.revokeObjectURL(htmlUrl);
        }
        return;
      } catch (linkError) {
        // Final fallback: use window.open
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
    }

    errorService.error(error);
  }
};

const downloadFile = async (
  url: string,
  mediaType: "image" | "video",
  organization?: { name: string }
): Promise<void> => downloadMediaFile({ url, mediaType, organization });

export { downloadMediaFile, downloadFile };
export default downloadMediaFile;
